import {useMemo} from 'react';
import {BodySite, BodySiteId, Pathology, PathologyId, UserId} from '@legit.health/ui';
import Patient, {Condition} from '@/practitioner/interfaces/Patient';
import Practitioner from '@/practitioner/interfaces/Practitioner';
import {useSharedDataContext} from '@/shared/components/layout/LoggedInLayout/contexts/SharedDataContext';
import {
  useGetPractitioner,
  useSuspenseGetPractitioner,
  useGetPractitionerRequests,
  useSuspenseGetPractitionerRequests
} from '../practitioner/api/useGetPractitioner';
import {PatientApiResponse} from './api/types';
import {useGetPatient, useSuspenseGetPatient} from './api/useGetPatient';

export function usePatient(patientId: UserId | null) {
  const {pathologiesById, bodySitesById} = useSharedDataContext();
  const patientRequest = useGetPatient(patientId);
  const patientRequestSucceeded = patientRequest.isSuccess;
  const patientRequestData = patientRequest.data?.data;
  const enabled = Boolean(patientRequest.data) && patientRequestSucceeded;
  const practitionerRequest = useGetPractitioner({
    practitionerId:
      patientRequestSucceeded && patientRequestData ? patientRequestData.practitionerId : null,
    isEnabled: enabled
  });
  const referredPractitionersRequests = useGetPractitionerRequests(
    patientRequestSucceeded && patientRequestData ? patientRequestData.referredToPractitioners : [],
    {
      enabled
    }
  );

  return useMemo(
    function () {
      const apiQueryStatus = {
        dataStatus: {
          isError:
            patientRequest.isError ||
            practitionerRequest.isError ||
            referredPractitionersRequests.some((request) => request.isError),
          isSuccess:
            patientRequest.isSuccess &&
            practitionerRequest.isSuccess &&
            referredPractitionersRequests.every((request) => request.isSuccess),
          isPending:
            patientRequest.isPending ||
            practitionerRequest.isPending ||
            referredPractitionersRequests.some((request) => request.isPending)
        },
        fetchStatus: {
          isFetching:
            patientRequest.isFetching ||
            practitionerRequest.isFetching ||
            referredPractitionersRequests.some((request) => request.isFetching),
          isPaused:
            patientRequest.isPaused &&
            practitionerRequest.isPaused &&
            referredPractitionersRequests.some((request) => request.isPaused),
          isIdle:
            patientRequest.fetchStatus === 'idle' &&
            practitionerRequest.fetchStatus === 'idle' &&
            referredPractitionersRequests.every((request) => request.fetchStatus === 'idle')
        }
      };
      const patient = patientRequest.data?.data;
      const practitioner = practitionerRequest.data?.data;
      const referredToPractitioners = referredPractitionersRequests.map(
        (request) => request.data?.data
      );
      const hasAllReferredToPractitioners = referredToPractitioners.every(
        (practitioner) => practitioner !== undefined
      );
      if (!patient || !practitioner || !hasAllReferredToPractitioners) {
        return {
          apiQueryStatus
        };
      }
      const fullPatient = createPatient(
        patient,
        practitioner,
        referredToPractitioners as Practitioner[],
        pathologiesById,
        bodySitesById
      );
      return {
        apiQueryStatus,
        patient: fullPatient,
        error:
          patientRequest?.error ||
          practitionerRequest?.error ||
          referredPractitionersRequests.map((request) => request?.error).find((error) => error)
      };
    },
    [
      practitionerRequest,
      patientRequest,
      referredPractitionersRequests,
      pathologiesById,
      bodySitesById
    ]
  );
}

export function useSuspensePatient(patientId: UserId) {
  const {pathologiesById, bodySitesById} = useSharedDataContext();
  const patientRequest = useSuspenseGetPatient(patientId);
  const patientRequestData = patientRequest.data.data;
  const practitionerRequest = useSuspenseGetPractitioner({
    practitionerId: patientRequestData.practitionerId
  });
  const referredPractitionersRequests = useSuspenseGetPractitionerRequests(
    patientRequestData.referredToPractitioners
  );

  return useMemo(
    function () {
      const patient = patientRequest.data.data;
      const practitioner = practitionerRequest.data.data;
      const referredToPractitioners = referredPractitionersRequests.map(
        (request) => request.data.data
      );
      const fullPatient = createPatient(
        patient,
        practitioner,
        referredToPractitioners as Practitioner[],
        pathologiesById,
        bodySitesById
      );
      return fullPatient;
    },
    [
      practitionerRequest,
      patientRequest,
      referredPractitionersRequests,
      pathologiesById,
      bodySitesById
    ]
  );
}

function createPatient(
  patient: PatientApiResponse,
  practitioner: Practitioner,
  referredToPractitioners: Practitioner[],
  pathologiesById: Record<PathologyId, Pathology>,
  bodySitesById: Record<BodySiteId, BodySite>
): Patient {
  const conditionsFilled = patient.conditions
    .filter((condition) => pathologiesById[condition.pathologyId])
    .map(
      (condition) =>
        new Condition(
          condition.id,
          bodySitesById[condition.bodySiteId] as BodySite,
          pathologiesById[condition.pathologyId] as Pathology,
          condition.practitionerId,
          condition.frequency,
          condition.isTracked,
          condition.canBeTracked,
          condition.assigmentSource,
          condition.firstImage,
          condition.firstImageThumbnail,
          condition.createdAt,
          condition.firstImageOfPerspective,
          condition.hasPhotoRequested,
          condition.clinicalTrial
        )
    )
    .slice()
    .sort((c1, c2) => c1.pathology.name.localeCompare(c2.pathology.name));
  return new Patient(
    patient.id,
    patient.companyId,
    patient.name,
    patient.surname,
    patient.secondSurname,
    patient.gender,
    patient.locale,
    patient.fullname,
    patient.country,
    patient.email,
    patient.image,
    patient.imageThumbnail,
    patient.isEnabled,
    patient.isDeleted,
    patient.birthDate,
    patient.telephone,
    patient.isReferred,
    patient.emergencyContact,
    patient.address,
    patient.languages,
    patient.careGaps,
    patient.socialDeterminants,
    patient.skinType,
    patient.riskFactors,
    patient.allergies,
    patient.weight,
    patient.height,
    patient.nhc,
    patient.idCard,
    patient.healthCenter,
    patient.healthCardNumber,
    patient.privateInsuranceProvider,
    patient.habits,
    conditionsFilled,
    practitioner,
    referredToPractitioners,
    patient.isAllowedToUploadPhotos,
    patient.assignedPathologies,
    patient.studyTerminatedAt,
    patient.studyTerminatedBy
  );
}
